import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Container,
  Typography,
  Paper,
  List,
  ListItem,
  ListItemText,
  CircularProgress,
  Divider,
} from '@mui/material';
import type { Order } from '../store/slices/orderSlice';
import api from '../services/api';
import { PayNowButton } from '../components/PayNowButton';
import PaymentSkeleton from '../components/skeletons/PaymentSkeleton';
// Note: This component appears to be unused in the current flow
// Users go: PublicMenu → ReadyToPay → External Payment → PaymentCallback → PaymentSuccess/Cancel

const Payment = () => {
  const { orderId } = useParams<{ orderId: string }>();
  const navigate = useNavigate();
  const [order, setOrder] = useState<Order | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchOrder = async () => {
      try {
        const response = await api.get(`/orders/${orderId}`);
        setOrder(response.data);
      } catch (error) {
        console.error('Error fetching order:', error);
        setError('Failed to fetch order details');
      } finally {
        setLoading(false);
      }
    };

    if (orderId) {
      fetchOrder();
    } else {
      setError('Missing order ID');
      setLoading(false);
    }
  }, [orderId]);

  if (loading) {
    return <PaymentSkeleton />;
  }

  if (error) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography color="error">{error}</Typography>
      </Box>
    );
  }

  if (!order) {
    return (
      <Box sx={{ mt: 4 }}>
        <Typography>Order not found</Typography>
      </Box>
    );
  }

  // Fallbacks for public order structure
  const items = Array.isArray(order.items) ? order.items : [];
  const total = typeof order.total === 'number' ? order.total : 0;
  const tax = total * 0.1; // 10% tax
  const finalTotal = total + tax;

  return (
    <Box sx={{ minHeight: '100vh', width: '100vw', background: '#f5f5f5', py: 4 }}>
      <Container maxWidth="sm" sx={{ pb: { xs: 10, sm: 4 } }}>
        <Box sx={{ mt: 4, mb: 3, textAlign: 'center' }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            Checkout & Payment
          </Typography>
          <Typography variant="subtitle1" color="text.secondary">
            Review your order and pay securely
          </Typography>
        </Box>
        <Paper sx={{ p: 3, mb: 3, borderRadius: 3, boxShadow: 2 }}>
          <Typography variant="h6" gutterBottom>
            Order Summary
          </Typography>
          <List>
            {items.map((item) => (
              <ListItem key={item.id} sx={{ px: 0 }}>
                <ListItemText
                  primary={<Typography fontWeight={600} component="span">{item.name}</Typography>}
                  secondary={`Quantity: ${item.quantity}`}
                />
                                        <Typography fontWeight={600}>₡{(item.price * item.quantity).toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
              </ListItem>
            ))}
          </List>
          <Divider sx={{ my: 2 }} />
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Subtotal:</Typography>
                                    <Typography>₡{total.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
            <Typography>Tax (10%):</Typography>
                                    <Typography>₡{tax.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
          </Box>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 3 }}>
            <Typography variant="h6">Total:</Typography>
                                    <Typography variant="h6">₡{finalTotal.toLocaleString(undefined, { minimumFractionDigits: 0, maximumFractionDigits: 0 })}</Typography>
          </Box>
          <PayNowButton
            orderId={order.id}
            currency={order.currency || 'CRC'}
            restaurantId={order.restaurantId}
            onSuccess={() => {
              navigate(`/payment/success?orderId=${order.id}`);
            }}
            onCancel={() => {
              navigate(`/payment/cancel?orderId=${order.id}`);
            }}
          />
        </Paper>
      </Container>
    </Box>
  );
};

export default Payment; 